import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { ProgressBar } from 'primereact/progressbar';
import { Skeleton } from 'primereact/skeleton';
import { InputText } from 'primereact/inputtext';
import API_BASE_URL from '../config/api';
import '../styles/TierSelection.css';

const TierSelectionPage = () => {
  const { school } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toastRef = useRef(null);
  
  // Get data from previous page
  const { submissionId, submissionData } = location.state || {};
  
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [schoolName, setSchoolName] = useState('');
  const [seedingMode, setSeedingMode] = useState(false);
  const [nationalDecisionDayPromo, setNationalDecisionDayPromo] = useState(false);
  const [instantOnlyPricing, setInstantOnlyPricing] = useState(false);
  
  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeValidated, setPromoCodeValidated] = useState(false);
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);
  const [promoIsFree, setPromoIsFree] = useState(true);
  const [promoDiscountAmount, setPromoDiscountAmount] = useState(0);
  const [promoDiscountDisplay, setPromoDiscountDisplay] = useState('');

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (school === 'streamer-university') {
      navigate('/streamer/tier-selection', { replace: true, state: location.state });
    }
  }, [school, navigate]);

  // Fetch school name
  useEffect(() => {
    const fetchSchoolName = async () => {
      if (!school) return;
      
      try {
        const response = await fetch(`${API_BASE_URL}/api/schools/${school}`);
        const result = await response.json();
        
        if (result.status === 'success') {
          setSchoolName(result.data.school_name);
        } else {
          setSchoolName(school.toUpperCase());
        }
      } catch (error) {
        console.error('Error fetching school name:', error);
        setSchoolName(school.toUpperCase());
      }
    };

    fetchSchoolName();
  }, [school]);

  // Fetch tier pricing
  useEffect(() => {
    const fetchPricing = async () => {
      if (!school) return;
      
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/pricing/${school}`);
        const result = await response.json();
        
        if (result.status === 'success') {
          setTiers(result.data.tiers);
          setSeedingMode(result.data.seedingMode);
          setNationalDecisionDayPromo(!!result.data.nationalDecisionDayPromo);
          setInstantOnlyPricing(!!result.data.instantOnlyPricing);
          
          // Instant-only / Decision Day / seeding: pre-select Instant
          // Legacy multi-tier: pre-select tier 2 (24 hours)
          if (result.data.instantOnlyPricing || result.data.nationalDecisionDayPromo || result.data.seedingMode) {
            const instantTier = result.data.tiers.find(t => t.isInstant) || result.data.tiers[0];
            if (instantTier) {
              setSelectedTier(instantTier.id);
            }
          } else {
            const defaultTier = result.data.tiers.find(t => t.id === 2);
            if (defaultTier) {
              setSelectedTier(defaultTier.id);
            }
          }
        } else {
          throw new Error(result.message || 'Failed to fetch pricing');
        }
      } catch (error) {
        console.error('Error fetching pricing:', error);
        toastRef.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load pricing. Please try again.',
          life: 5000
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPricing();
  }, [school]);

  // Verify submission has photos on mount
  useEffect(() => {
    const verifySubmission = async () => {
      if (!submissionId || !submissionData) {
        console.log('Missing submissionId or submissionData, redirecting to submission form');
        navigate(`/submit/${school}`);
        return;
      }
      
      // First check if photos are in state (coming from PhotoUploadPage)
      const photos = location.state?.photos;
      if (photos && photos.length > 0) {
        console.log('Photos found in state, assuming they are uploaded');
        return; // Trust that photos were uploaded
      }
      
      // If no photos in state, verify with backend (e.g., page refresh or direct navigation)
      try {
        console.log('No photos in state, verifying with backend...');
        const response = await fetch(`${API_BASE_URL}/api/submissions/${submissionId}/verify`);
        const result = await response.json();
        
        if (result.status === 'error' || !result.data?.hasPhotos) {
          console.log('Submission has no photos in database, redirecting to photo upload');
          toastRef.current?.show({
            severity: 'warn',
            summary: 'Photos Required',
            detail: 'Please upload photos before selecting a tier.',
            life: 3000
          });
          setTimeout(() => {
            navigate(`/submit/${school}/photos`, { 
              state: { submissionData, submissionId } 
            });
          }, 1000);
        } else {
          console.log(`Verified: submission has ${result.data.photoCount} photos`);
        }
      } catch (error) {
        console.error('Error verifying submission:', error);
        // If verification fails and no photos in state, redirect
        toastRef.current?.show({
          severity: 'warn',
          summary: 'Photos Required',
          detail: 'Please upload photos before selecting a tier.',
          life: 3000
        });
        setTimeout(() => {
          navigate(`/submit/${school}/photos`, { 
            state: { submissionData, submissionId } 
          });
        }, 1000);
      }
    };
    
    verifySubmission();
  }, [submissionId, submissionData, navigate, school, location.state]);

  const handleTierSelect = (tierId) => {
    setSelectedTier(tierId);
  };

  const handleValidatePromo = async () => {
    if (!promoCode.trim()) {
      toastRef.current?.show({
        severity: 'warn',
        summary: 'Enter Promo Code',
        detail: 'Please enter a promo code.',
        life: 3000
      });
      return;
    }

    setIsValidatingPromo(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/promo-codes/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: promoCode.trim(),
          school: school
        }),
      });

      const result = await response.json();

      if (result.status === 'success') {
        setPromoCodeValidated(true);
        setSelectedTier('promo'); // Select the promo tier
        
        // Check if it's a free or discount code
        const isFree = result.data.is_free;
        setPromoIsFree(isFree);
        setPromoDiscountAmount(result.data.discount_amount || 0);
        setPromoDiscountDisplay(result.data.discount_display || 'FREE');
        
        toastRef.current?.show({
          severity: 'success',
          summary: 'Promo Code Valid!',
          detail: isFree 
            ? 'Free instant posting unlocked! Select the Free tier below.' 
            : `${result.data.discount_display} instant posting unlocked! Select the discounted tier below.`,
          life: 5000
        });
      } else {
        toastRef.current?.show({
          severity: 'error',
          summary: 'Invalid Promo Code',
          detail: result.message || 'This promo code is not valid.',
          life: 5000
        });
      }
    } catch (error) {
      console.error('Promo validation error:', error);
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to validate promo code. Please try again.',
        life: 5000
      });
    } finally {
      setIsValidatingPromo(false);
    }
  };

  const handleContinue = async () => {
    // Guard against multiple simultaneous submissions
    if (isProcessing) {
      console.log('⚠️ Payment processing already in progress, ignoring duplicate attempt');
      return;
    }
    
    if (!selectedTier) {
      toastRef.current?.show({
        severity: 'warn',
        summary: 'Select a Tier',
        detail: 'Please select a posting speed to continue.',
        life: 3000
      });
      return;
    }

    console.log('🚀 Processing tier selection:', selectedTier);
    setIsProcessing(true);

    try {
      // If promo tier selected, apply promo code
      if (selectedTier === 'promo' && promoCodeValidated) {
        const applyResponse = await fetch(`${API_BASE_URL}/api/promo-codes/apply`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            code: promoCode.trim(),
            school: school,
            submissionId: submissionId
          }),
        });

        if (!applyResponse.ok) {
          const errorData = await applyResponse.json();
          throw new Error(errorData.message || 'Failed to apply promo code');
        }

        const applyResult = await applyResponse.json();

        // Check if this is a free promo or discount promo
        if (applyResult.data.isFree) {
          // FREE promo - go directly to posting status
          toastRef.current?.show({
            severity: 'success',
            summary: 'Promo Code Applied!',
            detail: 'Your post will be published instantly for free!',
            life: 3000
          });
          
          // Redirect to posting status page
          setTimeout(() => {
            navigate(`/posting-status/${submissionId}`);
          }, 1500);
          return;
        } else {
          // DISCOUNT promo - need to proceed to Stripe checkout
          toastRef.current?.show({
            severity: 'info',
            summary: 'Discount Applied!',
            detail: `Redirecting to checkout for ${applyResult.data.discountDisplay}...`,
            life: 3000
          });
          
          // Create checkout session with promo code applied (will use discounted price)
          const checkoutResponse = await fetch(`${API_BASE_URL}/api/stripe/create-checkout-session`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              submissionId: submissionId,
              tierId: 1 // Instant tier
            }),
          });

          if (!checkoutResponse.ok) {
            const errorData = await checkoutResponse.json();
            throw new Error(errorData.message || 'Failed to create checkout session');
          }

          const checkoutResult = await checkoutResponse.json();
          
          // Redirect to Stripe Checkout
          window.location.href = checkoutResult.url;
          return;
        }
      }

      // Create Stripe checkout session with selected tier
      const response = await fetch(`${API_BASE_URL}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          submissionId: submissionId,
          tierId: selectedTier
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create checkout session');
      }

      const result = await response.json();
      
      // Redirect to Stripe Checkout
      window.location.href = result.url;

    } catch (error) {
      console.error('Checkout error:', error);
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.message || 'Failed to proceed to payment. Please try again.',
        life: 5000
      });
      setIsProcessing(false);
    }
  };

  const handleBack = () => {
    navigate(`/submit/${school}/photos`, { 
      state: { submissionData, photos: location.state?.photos } 
    });
  };

  const getTierBadge = (tierId) => {
    if (tierId === 2) return { text: '⭐ Most popular', className: 'badge-popular' };
    if (tierId === 3) return { text: '✓ Best deal', className: 'badge-deal' };
    return null;
  };

  if (!submissionId || !submissionData) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="tier-selection-page">
      <Toast ref={toastRef} />
      
      {/* Header */}
      <header className="form-header">
        <div className="header-content">
          <Button
            icon="pi pi-arrow-left"
            label="Back"
            className="back-button"
            onClick={handleBack}
            disabled={isProcessing}
          />
          <div className="header-title">
            <p>{schoolName || 'Your University'}</p>
          </div>
        </div>
      </header>

      {/* Progress */}
      <div className="progress-section">
        <div className="container">
          <div className="progress-info">
            <span>{instantOnlyPricing ? 'Final Step: Confirm Instant Post' : 'Final Step: Choose Your Posting Speed'}</span>
          </div>
          <ProgressBar 
            value={90} 
            className="form-progress"
            showValue={false}
          />
        </div>
      </div>

      {/* Main Content */}
      <main className="tier-selection-main">
        <div className="container">
          
          {nationalDecisionDayPromo && !promoCodeValidated && (
            <div className="seeding-mode-notice decision-day-notice">
              <i className="pi pi-info-circle"></i>
              <span>
                National Decision Day: $5 instant post only — today (Eastern Time).
              </span>
            </div>
          )}

          {instantOnlyPricing && !nationalDecisionDayPromo && !promoCodeValidated && (
            <div className="seeding-mode-notice">
              <i className="pi pi-info-circle"></i>
              <span>Your post will go live instantly for $5.</span>
            </div>
          )}

          {seedingMode && !instantOnlyPricing && !nationalDecisionDayPromo && !promoCodeValidated && (
            <div className="seeding-mode-notice">
              <i className="pi pi-info-circle"></i>
              <span>🚀 Special Launch Pricing! Your post will be published instantly.</span>
            </div>
          )}

          <Card className="tier-selection-card">
            <h2 className="tier-title">
              {instantOnlyPricing ? 'Instant Posting' : 'Choose Your Posting Speed'}
            </h2>
            <p className="tier-subtitle">
              {instantOnlyPricing
                ? 'Confirm your $5 Instant post to go live right away'
                : "Select when you'd like your post to go live"}
            </p>

            {/* Promo Code Section */}
            <div className="promo-code-section">
              <div className="promo-code-input-group">
                <InputText
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder="Have a promo code?"
                  disabled={promoCodeValidated || isValidatingPromo}
                  className="promo-code-input"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleValidatePromo();
                    }
                  }}
                />
                <Button
                  label={promoCodeValidated ? 'Applied!' : isValidatingPromo ? 'Checking...' : 'Apply'}
                  onClick={handleValidatePromo}
                  disabled={promoCodeValidated || isValidatingPromo || !promoCode.trim()}
                  className={promoCodeValidated ? 'p-button-success promo-apply-button' : 'promo-apply-button'}
                />
              </div>
              {promoCodeValidated && (
                <div className="promo-success-message">
                  <i className="pi pi-check-circle"></i>
                  <span>Promo code valid! Select the Free tier below to continue.</span>
                </div>
              )}
            </div>

            {loading ? (
              <div className="tier-skeleton">
                {[1].map(i => (
                  <div key={i} className="skeleton-tier">
                    <Skeleton width="100%" height="120px" borderRadius="12px" />
                  </div>
                ))}
              </div>
            ) : (
              <div className={`tier-list ${instantOnlyPricing ? 'tier-list-single' : ''}`}>
                {/* Promo Tier (only shown when promo is validated) */}
                {promoCodeValidated && (
                  <div
                    className={`tier-option tier-promo ${selectedTier === 'promo' ? 'selected' : ''}`}
                    onClick={() => handleTierSelect('promo')}
                  >
                    <div className="tier-header">
                      <div className="tier-name-section">
                        <h3 className="tier-name">🎉 {promoIsFree ? 'Free' : 'Discounted'} - Instant</h3>
                      </div>
                      <div className={`tier-price ${promoIsFree ? 'tier-price-free' : 'tier-price-discount'}`}>
                        {promoDiscountDisplay}
                      </div>
                    </div>
                    
                    <div className="tier-description">
                      {promoIsFree 
                        ? 'Post immediately at no cost with your promo code!' 
                        : `Post immediately at a special discounted price with your promo code!`}
                    </div>
                    
                    <div className="tier-badge-promo-wrapper">
                      <span className="tier-badge badge-promo">
                        ✓ Promo Code Applied
                      </span>
                    </div>
                    
                    {selectedTier === 'promo' && (
                      <div className="tier-selected-indicator">
                        <i className="pi pi-check-circle"></i>
                      </div>
                    )}
                  </div>
                )}

                {/* Regular Tiers */}
                {tiers.map(tier => {
                  const badge = getTierBadge(tier.id);
                  const isSelected = selectedTier === tier.id;
                  const isDisabled = promoCodeValidated;
                  
                  return (
                    <div
                      key={tier.id}
                      className={`tier-option ${isSelected ? 'selected' : ''} ${isDisabled ? 'tier-disabled' : ''}`}
                      onClick={() => !isDisabled && handleTierSelect(tier.id)}
                    >
                      <div className="tier-header">
                        <div className="tier-name-section">
                          <h3 className="tier-name">{tier.name}</h3>
                          {badge && !seedingMode && !instantOnlyPricing && !nationalDecisionDayPromo && (
                            <span className={`tier-badge ${badge.className}`}>
                              {badge.text}
                            </span>
                          )}
                          {instantOnlyPricing && !promoCodeValidated && (
                            <span className="tier-badge badge-popular">
                              Instant
                            </span>
                          )}
                        </div>
                        <div className="tier-price">
                          {tier.originalPriceDisplay && !instantOnlyPricing ? (
                            <div className="tier-price-discount">
                              <span className="tier-price-original">{tier.originalPriceDisplay}</span>
                              <span className="tier-price-discounted">{tier.finalPriceDisplay}</span>
                            </div>
                          ) : (
                            tier.finalPriceDisplay
                          )}
                        </div>
                      </div>
                      
                      <div className="tier-description">
                        {instantOnlyPricing
                          ? 'Post immediately — highest priority'
                          : tier.description}
                      </div>
                      
                      {isSelected && (
                        <div className="tier-selected-indicator">
                          <i className="pi pi-check-circle"></i>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Continue Button */}
            <div className="tier-actions">
              <Button
                label={
                  isProcessing 
                    ? 'Processing...' 
                    : selectedTier === 'promo' 
                      ? (promoIsFree ? 'Submit Post (Free)' : `Continue to Payment (${promoDiscountDisplay})`)
                      : instantOnlyPricing
                        ? 'Continue to Payment — $5'
                        : 'Continue to Payment'
                }
                icon={isProcessing ? 'pi pi-spin pi-spinner' : 'pi pi-arrow-right'}
                iconPos="right"
                onClick={handleContinue}
                disabled={isProcessing || loading || !selectedTier}
                className="tier-continue-button"
              />
              <p className="tier-note">
                {selectedTier === 'promo' 
                  ? (promoIsFree 
                      ? 'Your post will be published instantly at no cost!' 
                      : `You'll be redirected to pay ${promoDiscountDisplay} for instant posting.`)
                  : instantOnlyPricing || nationalDecisionDayPromo
                    ? "You'll be redirected to secure payment. Your post will be published instantly."
                    : "You'll be redirected to secure payment. Your post will be published according to the selected tier."}
              </p>
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default TierSelectionPage;

